import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import { Box, Button, Stack } from "@mui/joy";
import { LinkOptions } from "@tanstack/react-router";
import { ApiQuest, ApiQuestDetail } from "griptonite-api-client/types";
import { ReactNode, useMemo, useState } from "react";

import { ButtonLink } from "../Links";
import getPublishWarnings from "./getPublishWarnings";

export const QUEST_PATH = "/gym/$venueUuid/loyalty/quests/$questUuid";

// The tabs in the order a draft quest steps through them.
export const QUEST_STEPS = ["details", "rules", "promotion"] as const;
export type QuestStep = (typeof QUEST_STEPS)[number];

export const questStepLink = (step: QuestStep): LinkOptions => ({
  to: `${QUEST_PATH}/profile/${step}`,
  replace: true,
});

// While a quest is a draft, each tab steps on to the next, and the last offers to publish it.
// Once it's published, each tab just saves.
const BottomButtons = ({
  quest,
  step,
  formState,
  onPublishClick,
}: {
  /** Absent while creating the quest. */
  quest?: ApiQuest | ApiQuestDetail;
  step: QuestStep;
  formState: { isSubmitting: boolean; isDirty: boolean };
  onPublishClick?: () => void;
}) => {
  const isWizardMode = !quest || quest.isHidden;
  const stepIndex = QUEST_STEPS.indexOf(step);
  const prevStep = QUEST_STEPS[stepIndex - 1];
  const nextStep = QUEST_STEPS[stepIndex + 1];
  const isFinalStep = !nextStep;

  const publishWarnings = useMemo(() => getPublishWarnings(quest), [quest]);

  // Which of the final step's two buttons submitted, so only that one shows as loading.
  const [didClickPublish, setDidClickPublish] = useState(false);

  let nextButton: ReactNode = null;
  if (!isWizardMode || (!isFinalStep && formState.isDirty)) {
    nextButton = (
      <Button
        type="submit"
        size="lg"
        color="primary"
        loading={formState.isSubmitting}
        loadingPosition="start"
        endDecorator={isWizardMode ? <KeyboardArrowRight /> : null}
        disabled={!formState.isDirty}
      >
        {isWizardMode ? "Save and Continue" : "Save"}
      </Button>
    );
  } else if (isFinalStep) {
    nextButton = (
      <>
        <Button
          type="submit"
          size="lg"
          color="primary"
          variant="outlined"
          disabled={!formState.isDirty}
          loading={formState.isSubmitting && !didClickPublish}
          loadingPosition="start"
          onClick={() => setDidClickPublish(false)}
          sx={{ marginRight: 1.5 }}
        >
          Save without publishing
        </Button>
        <Button
          type="button"
          size="lg"
          color="primary"
          loading={formState.isSubmitting && didClickPublish}
          loadingPosition="start"
          disabled={publishWarnings.allWarnings.length > 0}
          onClick={() => {
            onPublishClick?.();
            setDidClickPublish(true);
          }}
        >
          Publish
        </Button>
      </>
    );
  } else {
    nextButton = (
      <ButtonLink
        size="lg"
        color="primary"
        endDecorator={<KeyboardArrowRight />}
        {...questStepLink(nextStep)}
      >
        Continue
      </ButtonLink>
    );
  }

  return (
    <Stack direction="row" alignItems="center">
      {prevStep && isWizardMode ? (
        <ButtonLink
          size="lg"
          color="neutral"
          startDecorator={<KeyboardArrowLeft />}
          {...questStepLink(prevStep)}
        >
          Back
        </ButtonLink>
      ) : null}
      <Box sx={{ flex: 1 }} />
      {nextButton}
    </Stack>
  );
};

export default BottomButtons;
